import { FastColor } from '@ant-design/fast-color'
import { theme } from 'antd'
import type { ConfigProviderProps } from 'antd'

import { readableTint } from './contrast'
import { palettes } from './palette'
import type { ThemeMode } from './palette'
import { darkThemeTokens, themeTokens } from './themeTokens'

type TagConfig = NonNullable<ConfigProviderProps['tag']>

export type SoftTagStyle = {
  backgroundColor: string
  color: string
  borderColor: string
}

const PRESET_FILL_KEY = /^([a-z]+)6$/
const CUSTOM_COLOR =
  /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$|^(rgb|hsl)a?\(/i

const namedFillsFor = (mode: ThemeMode) => {
  const token = theme.getDesignToken({
    algorithm: mode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: mode === 'dark' ? darkThemeTokens : themeTokens
  })
  const fills = new Map<string, string>([
    ['success', token.colorSuccess],
    ['processing', token.colorInfo],
    ['error', token.colorError],
    ['warning', token.colorWarning]
  ])

  for (const [key, value] of Object.entries(token)) {
    const preset = PRESET_FILL_KEY.exec(key)?.[1]

    if (preset && typeof value === 'string') fills.set(preset, value)
  }

  return fills
}

const namedFills: Partial<Record<ThemeMode, Map<string, string>>> = {}

const opaqueOver = (backdrop: string, color: string) => {
  const parsed = new FastColor(color)

  return new FastColor(backdrop)
    .mix(parsed.setA(1), parsed.a * 100)
    .toHexString()
}

export const softTagStyle = (
  color: string | undefined,
  mode: ThemeMode
): SoftTagStyle | undefined => {
  if (!color) return undefined

  const fill =
    (namedFills[mode] ??= namedFillsFor(mode)).get(color) ??
    (CUSTOM_COLOR.test(color) ? color : undefined)

  if (!fill) return undefined

  const { background, text, border } = readableTint(
    opaqueOver(palettes[mode].container, fill),
    mode
  )

  return { backgroundColor: background, color: text, borderColor: border }
}

const softTagStylesFor =
  (mode: ThemeMode): TagConfig['styles'] =>
  ({ props }) => {
    if (props.variant === 'solid' || props.disabled) return {}

    const root = softTagStyle(props.color, mode)

    return root ? { root, close: { color: root.color } } : {}
  }

const tagConfigs: Partial<Record<ThemeMode, TagConfig>> = {}

export const softTagConfig = (mode: ThemeMode): TagConfig =>
  (tagConfigs[mode] ??= {
    variant: 'outlined',
    styles: softTagStylesFor(mode)
  })
